import React, { Component, ReactNode } from "react";
import i18next from "i18next";

import { formatErrorForDisplay } from "@/utils/errorFormatter";
import { logger } from "@/telemetry";

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorEntry {
  message: string;
  timestamp: Date;
  id: string; // Unique ID for tracking auto-dismiss timers
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  showStack: boolean;
  errors: ErrorEntry[];
}

// Network error patterns to classify as connection issues
const NETWORK_ERROR_PATTERNS = [
  "Failed to fetch",
  "Network Error",
  "Unable to make request",
  "TypeError: Failed to fetch",
  "net::ERR_",
  "ECONNREFUSED",
  "ETIMEDOUT",
  "NetworkError",
  "fetch failed",
];

// Check if an error message indicates a network issue
const isNetworkError = (message: string): boolean => {
  const lowerMessage = message.toLowerCase();

  return NETWORK_ERROR_PATTERNS.some((pattern) => lowerMessage.includes(pattern.toLowerCase()));
};

// Generate unique ID for error entries
const generateErrorId = (): string => {
  return `error-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// Auto-dismiss timeout in milliseconds (30 seconds)
const AUTO_DISMISS_TIMEOUT = 30000;

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private errorHandled: boolean = false;
  private isMounted: boolean = false;
  private autoDismissTimers: Map<string, NodeJS.Timeout> = new Map();

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      showStack: false,
      errors: [],
    };

    // Add global error handler for uncaught errors
    window.onerror = (message, source, lineno, colno, error) => {
      // Use formatter to extract meaningful error messages
      const errorMessage =
        typeof message === "string"
          ? message
          : error
            ? formatErrorForDisplay(error)
            : "Unknown error occurred";

      // Filter out ResizeObserver errors
      if (errorMessage.includes("ResizeObserver loop completed with undelivered notifications")) {
        return false; // Ignore ResizeObserver warnings
      }

      if (!this.errorHandled) {
        const errorSource = source ? ` at ${source}:${lineno}:${colno}` : "";

        this.handleError(error || new Error(`${errorMessage}${errorSource}`));
      }

      return false;
    };

    // Add handler for unhandled promise rejections
    window.onunhandledrejection = (event) => {
      if (!this.errorHandled) {
        // Use formatter to extract meaningful error messages
        const errorMessage =
          event.reason instanceof Error
            ? event.reason.message
            : formatErrorForDisplay(event.reason);

        this.handleError(event.reason instanceof Error ? event.reason : new Error(errorMessage));
      }
    };
  }

  componentDidMount() {
    this.isMounted = true;

    // Clear errors on navigation - add listeners after component is mounted
    window.addEventListener("popstate", this.clearError);
    window.addEventListener("beforeunload", this.clearError);
    // Allow external systems (e.g., auth) to request clearing
    window.addEventListener("app:clear-errors", this.clearError);
  }

  componentWillUnmount() {
    this.isMounted = false;

    // Remove event listeners
    window.removeEventListener("popstate", this.clearError);
    window.removeEventListener("beforeunload", this.clearError);
    window.removeEventListener("app:clear-errors", this.clearError);
  }

  addError = (message: string) => {
    const errorId = generateErrorId();
    const newError: ErrorEntry = { message, timestamp: new Date(), id: errorId };

    this.setState((prevState) => ({
      errors: [...prevState.errors, newError],
    }));

    // Start auto-dismiss timer
    this.startAutoDismissTimer(errorId);
  };

  // Start auto-dismiss timer for an error
  startAutoDismissTimer = (errorId: string) => {
    const timer = setTimeout(() => {
      this.clearErrorById(errorId);
    }, AUTO_DISMISS_TIMEOUT);

    this.autoDismissTimers.set(errorId, timer);
  };

  // Clear auto-dismiss timer
  clearAutoDismissTimer = (errorId: string) => {
    const timer = this.autoDismissTimers.get(errorId);

    if (timer) {
      clearTimeout(timer);
      this.autoDismissTimers.delete(errorId);
    }
  };

  // Clear all auto-dismiss timers
  clearAllAutoDismissTimers = () => {
    this.autoDismissTimers.forEach((timer) => clearTimeout(timer));
    this.autoDismissTimers.clear();
  };

  // Clear error by ID
  clearErrorById = (errorId: string) => {
    this.clearAutoDismissTimer(errorId);

    if (this.isMounted) {
      this.setState((prevState) => {
        const newErrors = prevState.errors.filter((err) => err.id !== errorId);

        // If no errors left, also clear hasError state
        if (newErrors.length === 0) {
          return {
            errors: newErrors,
            hasError: false,
            error: null,
            showStack: false,
          };
        }

        return {
          ...prevState,
          errors: newErrors,
        };
      });
    }
  };

  handleError = (error: Error): void => {
    if (!this.errorHandled) {
      this.errorHandled = true;
      logger.error("ErrorBoundary caught error", error);

      const errorId = generateErrorId();
      const newError: ErrorEntry = { message: error.message, timestamp: new Date(), id: errorId };

      this.setState((prevState) => ({
        hasError: true,
        error,
        showStack: false,
        errors: [...prevState.errors, newError],
      }));

      // Start auto-dismiss timer
      this.startAutoDismissTimer(errorId);

      this.errorHandled = false;
    }
  };

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    const errorObj = error instanceof Error ? error : new Error(String(error));

    return {
      hasError: true,
      error: errorObj,
      showStack: false,
      errors: [{ message: errorObj.message, timestamp: new Date(), id: generateErrorId() }],
    };
  }

  componentDidCatch(error: unknown, errorInfo: React.ErrorInfo): void {
    const errorObj = error instanceof Error ? error : new Error(String(error));

    logger.error("Error caught by boundary", errorObj, {
      componentStack: errorInfo.componentStack ?? "",
    });
    this.handleError(errorObj);
  }

  clearError = () => {
    // Clear all auto-dismiss timers
    this.clearAllAutoDismissTimers();

    // Only update state if component is mounted
    if (this.isMounted) {
      this.setState({
        hasError: false,
        error: null,
        showStack: false,
        errors: [],
      });
    }
  };

  clearSingleError = (index: number) => {
    const errorToRemove = this.state.errors[index];

    if (errorToRemove) {
      this.clearAutoDismissTimer(errorToRemove.id);
    }

    this.setState((prevState) => {
      const newErrors = prevState.errors.filter((_, i) => i !== index);

      // If no errors left, also clear hasError state
      if (newErrors.length === 0) {
        return {
          errors: newErrors,
          hasError: false,
          error: null,
          showStack: false,
        };
      }

      return {
        ...prevState,
        errors: newErrors,
      };
    });
  };

  render(): ReactNode {
    const { hasError, error, showStack, errors } = this.state;
    const t = i18next.t;

    if (hasError || errors.length > 0) {
      // Check if any errors are network-related for appropriate heading
      const hasNetworkErrors = errors.some((err) => isNetworkError(err.message));

      const headingText = hasNetworkErrors
        ? t("errorBanner.connectionIssues", { defaultValue: "Connection issues detected" })
        : t("errorBanner.somethingWentWrong", { defaultValue: "Something went wrong" });

      return (
        <>
          {this.props.children}
          <div
            className="fixed top-0 left-0 z-[9999] bg-brand-primary-soft border-l-4 border-brand-primary shadow-lg p-4 transition-all duration-300 ease-in-out animate-in slide-in-from-top"
            role="alert"
          >
            {/* Header row: icon + heading + close button */}
            <div className="flex items-center gap-2 mb-2">
              <svg
                aria-hidden="true"
                className="w-5 h-5 flex-shrink-0 text-red-600 dark:text-red-400"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  clipRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z"
                  fillRule="evenodd"
                />
              </svg>
              <h2 className="font-semibold text-sm sm:text-base text-red-600 dark:text-red-400">
                {headingText}
              </h2>
              <button
                aria-label={t("errorBanner.closeAll", { defaultValue: "Close all" })}
                className="ml-2 p-1 rounded-full hover:bg-black/10 dark:hover:bg-white/10 transition-colors"
                onClick={this.clearError}
              >
                <svg aria-hidden="true" className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    clipRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    fillRule="evenodd"
                  />
                </svg>
              </button>
            </div>

            {/* Error list */}
            <div className="space-y-1">
              {errors.map((err, index) => (
                <div key={err.id} className="flex items-center gap-2 py-1">
                  <p className="text-sm text-red-600 dark:text-red-400">{err.message}</p>
                  <span className="text-xs opacity-60">{err.timestamp.toLocaleTimeString()}</span>
                  <button
                    className="text-xs font-medium hover:underline whitespace-nowrap px-2 py-1 rounded hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                    onClick={() => this.clearSingleError(index)}
                  >
                    {t("errorBanner.dismiss", { defaultValue: "Dismiss" })}
                  </button>
                </div>
              ))}
            </div>

            {/* Stack trace toggle */}
            {error && (
              <div className="mt-3">
                <button
                  className="text-xs font-medium hover:underline"
                  onClick={() => this.setState({ showStack: !showStack })}
                >
                  {showStack
                    ? t("errorBanner.hideStackTrace", { defaultValue: "Hide Stack Trace" })
                    : t("errorBanner.showStackTrace", { defaultValue: "Show Stack Trace" })}
                </button>
                {showStack && (
                  <pre className="text-xs mt-2 p-3 rounded bg-default-100 dark:bg-default-900/50 overflow-auto max-h-40">
                    {error.stack}
                  </pre>
                )}
              </div>
            )}

            {/* Dismiss all button (only shown when multiple errors) */}
            {errors.length > 1 && (
              <button
                className="text-xs font-medium hover:underline mt-3 block"
                onClick={this.clearError}
              >
                {t("errorBanner.dismissAll", { defaultValue: "Dismiss All" })}
              </button>
            )}
          </div>
        </>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
